import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Loader2,
  MessageSquare,
  Tag,
} from "lucide-react";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useProjectContext } from "~/components/layout/project-context";
import { ExclusionReasonDialog } from "~/components/screening/exclusion-reason-dialog";
import { ScreeningEmptyState } from "~/components/screening/screening-empty-state";
import { ScreeningActions } from "~/components/screening/screening-actions";
import { ScreeningHeader } from "~/components/screening/screening-header";
import { Button } from "~/components/ui/button";
import { Kbd } from "~/components/ui/kbd";
import { ScrollArea } from "~/components/ui/scroll-area";
import type { ConflictCitation, ConflictDetails } from "~/lib/screening/adjudication";
import { $submitAdjudication, $undoAdjudication } from "~/lib/screening/adjudication";
import {
  adjudicationQueueQueryOptions,
  citationNotesQueryOptions,
  citationTagsQueryOptions,
  conflictDetailsQueryOptions,
  exclusionCodesQueryOptions,
} from "~/lib/screening/queries";

const searchSchema = z.object({
  citationId: z.string().optional(),
});

export const Route = createFileRoute("/_auth/projects/$projectId/screening/title-abstract/conflicts")(
  {
    validateSearch: searchSchema,
    loader: async ({ context, params }) => {
      await Promise.all([
        context.queryClient.ensureQueryData(
          adjudicationQueueQueryOptions(params.projectId),
        ),
        context.queryClient.ensureQueryData(exclusionCodesQueryOptions(params.projectId)),
      ]);
    },
    component: ConflictsPage,
  },
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Author = { family: string; given?: string };

function formatAuthorsList(authors: Author[] | null): string[] {
  if (!authors || authors.length === 0) return ["Unknown authors"];
  return authors.map((a) => {
    if (!a.given) return a.family;
    const initials = a.given
      .split(" ")
      .map((p) => p.charAt(0))
      .join("");
    return `${a.family} ${initials}`;
  });
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function decisionLabel(decision: string): string {
  switch (decision) {
    case "include":
      return "Include";
    case "exclude":
      return "Exclude";
    case "maybe":
      return "Maybe";
    default:
      return decision;
  }
}

function decisionStyles(decision: string) {
  switch (decision) {
    case "include":
      return { border: "border-l-[#2B9A66]", bg: "bg-[#F4FBF6] dark:bg-emerald-950/30" };
    case "exclude":
      return { border: "border-l-[#DC3E42]", bg: "bg-[#FFF7F7] dark:bg-red-950/30" };
    case "maybe":
      return { border: "border-l-[#FFDC00]", bg: "bg-[#FEFCE9] dark:bg-yellow-950/30" };
    default:
      return { border: "border-l-muted-foreground", bg: "bg-muted" };
  }
}

function resolutionColor(resolution: "include" | "exclude" | "send_to_full_text") {
  switch (resolution) {
    case "include":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300";
    case "exclude":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
    case "send_to_full_text":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300";
  }
}

function resolutionLabel(resolution: "include" | "exclude" | "send_to_full_text") {
  switch (resolution) {
    case "include":
      return "Include";
    case "exclude":
      return "Exclude";
    case "send_to_full_text":
      return "Send to Full-Text";
  }
}

// ---------------------------------------------------------------------------
// Undo types
// ---------------------------------------------------------------------------

interface UndoEntry {
  citation: ConflictCitation;
  resolution: "include" | "exclude" | "send_to_full_text";
}

const MAX_UNDO_STACK = 10;

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function ConflictsPage() {
  const { projectId, projectTitle } = useProjectContext();
  const { citationId: searchCitationId } = Route.useSearch();
  const navigate = useNavigate();

  const { data: queue } = useSuspenseQuery(adjudicationQueueQueryOptions(projectId));

  // Capture the initial queue size so we can show progress
  const initialQueueSize = useRef(queue.length);
  if (queue.length > initialQueueSize.current) {
    initialQueueSize.current = queue.length;
  }

  const currentCitationId = useMemo(() => {
    if (searchCitationId && queue.some((c) => c.id === searchCitationId)) {
      return searchCitationId;
    }
    return queue[0]?.id;
  }, [queue, searchCitationId]);

  const goToNext = useCallback(() => {
    navigate({
      to: "/projects/$projectId/screening/title-abstract/conflicts",
      params: { projectId },
      search: {},
      replace: true,
    });
  }, [navigate, projectId]);

  const resolved = initialQueueSize.current - queue.length;
  const total = initialQueueSize.current;

  // Empty state
  if (queue.length === 0) {
    return (
      <div className="flex h-[100dvh] flex-col overflow-hidden">
        <main className="flex flex-1 px-6 py-10">
          <ScreeningEmptyState
            title="Nothing to screen right now"
            description="There are no title and abstract conflicts to adjudicate."
            actions={
              <Button render={<Link to="/projects/$projectId" params={{ projectId }} />}>
                Return to Project
              </Button>
            }
          />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      <ScreeningHeader
        remaining={queue.length}
        progress={{ resolved, total }}
      />

      {currentCitationId && (
        <Suspense
          fallback={
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          }
        >
          <AdjudicationContent
            projectId={projectId}
            citationId={currentCitationId}
            queue={queue}
            onResolved={goToNext}
          />
        </Suspense>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdjudicationContent — two-column layout + bottom bar
// ---------------------------------------------------------------------------

function AdjudicationContent({
  projectId,
  citationId,
  queue,
  onResolved,
}: {
  projectId: string;
  citationId: string;
  queue: ConflictCitation[];
  onResolved: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: conflict } = useSuspenseQuery(
    conflictDetailsQueryOptions(projectId, citationId),
  );
  const { data: codes } = useSuspenseQuery(exclusionCodesQueryOptions(projectId));

  const [resolution, setResolution] = useState<
    "include" | "exclude" | "send_to_full_text" | null
  >(null);
  const [rationale, setRationale] = useState("");
  const [exclusionCode, setExclusionCode] = useState("");
  const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);

  // Undo stack
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);
  const undoStackRef = useRef(undoStack);
  undoStackRef.current = undoStack;

  // Build a code→label lookup from the exclusion codes
  const codeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of codes) {
      map.set(c.code, c.label);
    }
    return map;
  }, [codes]);

  // Reset state when citationId changes
  useEffect(() => {
    setResolution(null);
    setRationale("");
    setExclusionCode("");
  }, [citationId]);

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "adjudication"],
    });
    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "screening"],
    });
    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "dual-screening"],
    });
    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "statistics"],
    });
  }, [queryClient, projectId]);

  const adjudicateMutation = useMutation({
    mutationFn: (data: {
      resolution: "include" | "exclude" | "send_to_full_text";
      rationale?: string;
      exclusionCode?: string;
    }) =>
      $submitAdjudication({
        data: {
          projectId,
          citationId,
          resolution: data.resolution,
          rationale: data.rationale,
          exclusionCode: data.exclusionCode,
        },
      }),
    onSuccess: (_result, variables) => {
      // Push to undo stack
      const citationData = queue.find((c) => c.id === citationId);
      if (citationData) {
        setUndoStack((prev) =>
          [{ citation: citationData, resolution: variables.resolution }, ...prev].slice(
            0,
            MAX_UNDO_STACK,
          ),
        );
      }

      invalidateAll();

      setResolution(null);
      setRationale("");
      setExclusionCode("");
      onResolved();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const undoMutation = useMutation({
    mutationFn: (undoCitationId: string) =>
      $undoAdjudication({
        data: { projectId, citationId: undoCitationId },
      }),
    onSuccess: (_result, undoCitationId) => {
      setUndoStack((prev) => prev.filter((e) => e.citation.id !== undoCitationId));
      invalidateAll();

      // Navigate to the restored citation
      navigate({
        to: "/projects/$projectId/screening/title-abstract/conflicts",
        params: { projectId },
        search: { citationId: undoCitationId },
        replace: true,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const isPending = adjudicateMutation.isPending || undoMutation.isPending;

  const handleInclude = useCallback(() => {
    if (!isPending && conflict.canAdjudicate) {
      adjudicateMutation.mutate({ resolution: "include" });
    }
  }, [isPending, conflict.canAdjudicate, adjudicateMutation]);

  const handleExclude = useCallback(() => {
    if (!isPending && conflict.canAdjudicate) {
      setExclusionDialogOpen(true);
    }
  }, [isPending, conflict.canAdjudicate]);

  const handleSendToFullText = useCallback(() => {
    if (!isPending && conflict.canAdjudicate) {
      adjudicateMutation.mutate({ resolution: "send_to_full_text" });
    }
  }, [isPending, conflict.canAdjudicate, adjudicateMutation]);

  const handleExclusionSubmit = useCallback(
    (code: string, _notes?: string) => {
      setExclusionCode(code);
      setExclusionDialogOpen(false);
      setResolution("exclude");
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (!resolution) return;
    adjudicateMutation.mutate({
      resolution,
      rationale: rationale.trim() || undefined,
      exclusionCode: resolution === "exclude" ? exclusionCode : undefined,
    });
  }, [resolution, rationale, exclusionCode, adjudicateMutation]);

  const handleCancelResolution = useCallback(() => {
    setResolution(null);
    setRationale("");
    setExclusionCode("");
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStack.length > 0 && !isPending) {
      undoMutation.mutate(undoStack[0].citation.id);
    }
  }, [undoStack, isPending, undoMutation]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        // Allow Ctrl/Cmd+Enter in textarea for submit
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
          e.preventDefault();
          handleSubmit();
        }
        return;
      }

      // Ctrl+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        handleUndo();
        return;
      }

      if (!conflict.canAdjudicate) return;

      // When in exclude rationale mode
      if (resolution) {
        if (e.key === "Escape") {
          e.preventDefault();
          handleCancelResolution();
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
          e.preventDefault();
          handleSubmit();
          return;
        }
        return;
      }

      // Default action shortcuts
      if (e.key === "y" || e.key === "Y") {
        e.preventDefault();
        handleInclude();
        return;
      }
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        handleExclude();
        return;
      }
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        handleSendToFullText();
        return;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [resolution, conflict.canAdjudicate, handleInclude, handleExclude, handleSendToFullText, handleCancelResolution, handleSubmit, handleUndo]);

  const { citation, decision1, decision2 } = conflict;

  return (
    <>
      {/* Two-column layout */}
      <div className="flex min-h-0 flex-1">
        {/* Left column — Citation content */}
        <div className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="min-h-0 flex-1 overflow-hidden">
            <main className="px-6 py-8">
              <article key={citationId} className="animate-fade-in-up mx-auto max-w-2xl">
                {/* Title */}
                <section className="mb-6">
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    Title
                  </span>
                  <h1 className="mt-1 font-serif text-3xl font-medium">{citation.title}</h1>
                </section>

                {/* Abstract */}
                <section>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                      Abstract
                    </span>
                    {citation.abstract && (
                      <span className="text-muted-foreground text-xs">
                        {countWords(citation.abstract)} words
                      </span>
                    )}
                  </div>
                  {citation.abstract ? (
                    <p className="text-[15px] leading-relaxed">{citation.abstract}</p>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">No abstract available</p>
                  )}
                </section>
              </article>
            </main>
          </ScrollArea>
        </div>

        {/* Right sidebar — same style as ScreeningSidebar */}
        <aside className="bg-muted/30 hidden w-72 shrink-0 border-l lg:block">
          <ScrollArea className="h-full">
            {/* Header */}
            <div className="border-b px-4 py-2">
              <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                Conflict Details
              </span>
            </div>

            {/* Reviewer decisions */}
            <section className="space-y-2 border-b p-4">
              <SidebarLabel>Decisions</SidebarLabel>
              <ReviewerDecisionBlock decision={decision1} codeLabels={codeLabels} />
              <ReviewerDecisionBlock decision={decision2} codeLabels={codeLabels} />
            </section>

            {/* Tags & Notes */}
            <CitationTagsNotes projectId={projectId} citationId={citationId} />

            {/* Citation metadata */}
            <CitationMetaSection citation={citation} />
          </ScrollArea>
        </aside>
      </div>

      {/* Bottom action bar */}
      {!resolution ? (
        <div className="bg-background shrink-0 border-t p-4">
          <div className="mx-auto max-w-2xl">
            {conflict.canAdjudicate ? (
              <AdjudicationActions
                onInclude={handleInclude}
                onExclude={handleExclude}
                onSendToFullText={handleSendToFullText}
                onUndo={handleUndo}
                canUndo={undoStack.length > 0}
                isPending={isPending}
              />
            ) : (
              <div className="flex items-center justify-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <AlertTriangle className="h-4 w-4" />
                {conflict.adjudicationReason}
              </div>
            )}
          </div>
        </div>
      ) : (
        <RationaleBar
          resolution={resolution}
          exclusionCode={exclusionCode}
          exclusionCodeLabel={exclusionCode ? codeLabels.get(exclusionCode) : undefined}
          rationale={rationale}
          onRationaleChange={setRationale}
          onCancel={handleCancelResolution}
          onSubmit={handleSubmit}
          isPending={isPending}
        />
      )}

      <Suspense fallback={null}>
        <ExclusionReasonDialog
          projectId={projectId}
          open={exclusionDialogOpen}
          onOpenChange={setExclusionDialogOpen}
          onSubmit={handleExclusionSubmit}
          isLoading={isPending}
        />
      </Suspense>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared sidebar label (matches ScreeningSidebar)
// ---------------------------------------------------------------------------

function SidebarLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Adjudication Actions — matches ScreeningActions style
// ---------------------------------------------------------------------------

function AdjudicationActions({
  onInclude,
  onExclude,
  onSendToFullText,
  onUndo,
  canUndo,
  isPending,
}: {
  onInclude: () => void;
  onExclude: () => void;
  onSendToFullText: () => void;
  onUndo: () => void;
  canUndo: boolean;
  isPending: boolean;
}) {
  return (
    <ScreeningActions
      onInclude={onInclude}
      onExclude={onExclude}
      onUndo={onUndo}
      canUndo={canUndo}
      isLoading={isPending}
    >
      <Button
        variant="outline"
        onClick={onSendToFullText}
        disabled={isPending}
      >
        <FileText className="mr-1.5 h-4 w-4" />
        Full-Text
        <Kbd className="ml-2">F</Kbd>
      </Button>
    </ScreeningActions>
  );
}

// ---------------------------------------------------------------------------
// Rationale Bar (shown after selecting a resolution)
// ---------------------------------------------------------------------------

function RationaleBar({
  resolution,
  exclusionCode,
  exclusionCodeLabel,
  rationale,
  onRationaleChange,
  onCancel,
  onSubmit,
  isPending,
}: {
  resolution: "include" | "exclude" | "send_to_full_text";
  exclusionCode: string;
  exclusionCodeLabel?: string;
  rationale: string;
  onRationaleChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  isPending: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const t = setTimeout(() => textareaRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="bg-background shrink-0 border-t p-4">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <span
                className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${resolutionColor(resolution)}`}
              >
                {resolutionLabel(resolution)}
                {resolution === "exclude" && exclusionCode && (
                  <span className="ml-1.5 font-normal opacity-75">
                    &mdash; {exclusionCodeLabel ?? exclusionCode}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={onCancel}
                className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 hover:underline"
              >
                Change
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={rationale}
              onChange={(e) => onRationaleChange(e.target.value)}
              placeholder="Rationale for your decision…"
              rows={2}
              className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <div className="flex flex-col gap-2 pt-7">
            <Button
              onClick={onSubmit}
              disabled={!rationale.trim() || isPending}
              size="sm"
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Submitting
                </>
              ) : (
                "Submit"
              )}
            </Button>
            <span className="text-muted-foreground text-center text-[10px]">
              <Kbd className="text-[9px]">⌘↵</Kbd>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reviewer Decision Block
// ---------------------------------------------------------------------------

function ReviewerDecisionBlock({
  decision,
  codeLabels,
}: {
  decision: ConflictDetails["decision1"];
  codeLabels: Map<string, string>;
}) {
  const styles = decisionStyles(decision.decision);
  const exclusionLabel = decision.exclusionCode
    ? codeLabels.get(decision.exclusionCode) ?? decision.exclusionCode
    : null;

  return (
    <div className={`border-l-[3px] ${styles.border} ${styles.bg} p-3`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{decision.reviewerName}</span>
        <span className="text-muted-foreground text-xs">
          {decisionLabel(decision.decision)}
        </span>
      </div>

      {exclusionLabel && (
        <p className="text-muted-foreground mt-1 text-xs">
          {exclusionLabel}
        </p>
      )}

      {(decision.exclusionNotes || decision.notes) && (
        <p className="text-muted-foreground mt-1.5 text-xs italic leading-relaxed">
          &ldquo;{decision.exclusionNotes || decision.notes}&rdquo;
        </p>
      )}

      <p className="text-muted-foreground mt-2 text-[11px]">
        {new Date(decision.createdAt).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Citation Tags & Notes
// ---------------------------------------------------------------------------

function CitationTagsNotes({
  projectId,
  citationId,
}: {
  projectId: string;
  citationId: string;
}) {
  const { data: tagsData } = useQuery(citationTagsQueryOptions(projectId, citationId));
  const { data: notesData } = useQuery(citationNotesQueryOptions(projectId, citationId));

  const tags = tagsData ?? [];
  const notes = notesData?.notes ?? [];

  if (tags.length === 0 && notes.length === 0) return null;

  return (
    <section className="border-b p-4">
      <SidebarLabel>Tags & Notes</SidebarLabel>

      {tags.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center gap-1.5">
            <Tag className="text-muted-foreground h-3 w-3 shrink-0" />
            <span className="text-muted-foreground text-[11px] font-medium">Tags</span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t.id}
                className="bg-muted inline-flex items-center rounded-md px-2 py-0.5 text-xs"
              >
                {t.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div className={tags.length > 0 ? "mt-4" : "mt-3"}>
          <div className="flex items-center gap-1.5">
            <MessageSquare className="text-muted-foreground h-3 w-3 shrink-0" />
            <span className="text-muted-foreground text-[11px] font-medium">Notes</span>
          </div>
          <div className="mt-1.5 space-y-2">
            {notes.map((note) => (
              <div key={note.id} className="text-xs">
                <span className="font-medium">{note.userName}</span>
                <p className="text-muted-foreground mt-0.5 whitespace-pre-wrap">
                  {note.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Citation Metadata (matches ScreeningSidebar DetailsSection style)
// ---------------------------------------------------------------------------

function CitationMetaSection({ citation }: { citation: ConflictDetails["citation"] }) {
  const authors = formatAuthorsList(citation.authors);
  const hasAnyMeta =
    citation.authors?.length ||
    citation.year ||
    citation.containerTitle ||
    citation.doi ||
    citation.pmid;

  if (!hasAnyMeta) return null;

  return (
    <section className="space-y-3 p-4">
      <div>
        <SidebarLabel>Authors</SidebarLabel>
        <div className="mt-1 space-y-0.5 text-sm">
          {authors.slice(0, 5).map((author, i) => (
            <p key={i}>{author}</p>
          ))}
          {authors.length > 5 && (
            <p className="text-muted-foreground">+{authors.length - 5} more</p>
          )}
        </div>
      </div>
      {citation.year && (
        <div>
          <SidebarLabel>Year</SidebarLabel>
          <p className="mt-1 text-sm">{citation.year}</p>
        </div>
      )}
      {citation.containerTitle && (
        <div>
          <SidebarLabel>Journal</SidebarLabel>
          <p className="mt-1 text-sm">{citation.containerTitle}</p>
        </div>
      )}
      {(citation.volume || citation.issue) && (
        <div>
          <SidebarLabel>Volume / Issue</SidebarLabel>
          <p className="mt-1 text-sm">
            {citation.volume}
            {citation.issue && `(${citation.issue})`}
          </p>
        </div>
      )}
      {citation.pages && (
        <div>
          <SidebarLabel>Pages</SidebarLabel>
          <p className="mt-1 text-sm">{citation.pages}</p>
        </div>
      )}
      {citation.doi && (
        <div>
          <SidebarLabel>DOI</SidebarLabel>
          <p className="mt-1">
            <a
              href={`https://doi.org/${citation.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 text-xs break-all hover:underline"
            >
              {citation.doi}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </p>
        </div>
      )}
      {citation.pmid && (
        <div>
          <SidebarLabel>PMID</SidebarLabel>
          <p className="mt-1">
            <a
              href={`https://pubmed.ncbi.nlm.nih.gov/${citation.pmid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
            >
              {citation.pmid}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </p>
        </div>
      )}
    </section>
  );
}
